import { EditorItemType, EditorStarterItem } from '../../items/item-type';
import { removeItemFromTracks } from '../../utils/add-item-to-track-index';
import {
  findSpaceForItem,
  FindSpaceStartPosition,
  Space,
} from '../../utils/find-space-for-item';
import { generateRandomId } from '../../utils/generate-random-id';
import { getZoneForItemType } from '../../utils/zone-utils';
import { EditorState, TrackType } from '../types';
import { itemOwner } from '@waka/compat/group-split';

const itemCategory = (item: EditorStarterItem) =>
  item.type === 'audio'
    ? 'audio'
    : item.type === 'captions' ||
        (item.type === 'text' && item.renderMeta?.clipType === 'caption')
      ? 'caption'
      : 'video';
const trackCategory = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
) =>
  track.zone === 'audio'
    ? 'audio'
    : track.metadata?.type === 'caption' || track.metadata?.type === 'captions'
      ? 'caption'
      : track.metadata?.type === 'video'
        ? 'video'
        : (track.items
            .map((id) => items[id])
            .filter(Boolean)
            .map(itemCategory)[0] ?? 'video');

const getTrackMetadataType = (itemType: EditorItemType): EditorItemType => {
  return itemType === 'captions' ? 'caption' : itemType;
};

const createNewTrack = (item: EditorStarterItem): TrackType => {
  const metadataType = getTrackMetadataType(item.type);
  return {
    id: generateRandomId(),
    items: [item.id],
    hidden: false,
    muted: false,
    locked: false,
    magnetic: false,
    zone: getZoneForItemType(item.type),
    metadata: {
      isMainTrack: false,
      trackType: null,
      type: metadataType,
    },
  };
};

export const addItemInSpace = ({
  tracks,
  item,
  space,
}: {
  tracks: TrackType[];
  item: EditorStarterItem;
  space: Space;
}): TrackType[] => {
  const withItemRemoved = removeItemFromTracks(tracks, item.id);

  if (!withItemRemoved[space.trackIndex]) {
    const newTrack = createNewTrack(item);

    if (space.trackIndex === -1) {
      return [newTrack, ...withItemRemoved];
    }

    return [...withItemRemoved, newTrack];
  }

  if (space.forceCreateNewTrack) {
    const previousTracks = withItemRemoved.slice(0, space.trackIndex);
    const newTrack = createNewTrack(item);
    return [
      ...previousTracks,
      newTrack,
      ...withItemRemoved.slice(space.trackIndex),
    ];
  }

  const newTracks = withItemRemoved.map((track, idx) => {
    if (idx === space.trackIndex) {
      return { ...track, items: [...track.items, item.id] };
    }

    return track;
  });

  return newTracks;
};

export const addItem = ({
  state,
  item,
  select,
  position: position,
}: {
  state: EditorState;
  item: EditorStarterItem;
  select: boolean;
  position: FindSpaceStartPosition;
}): EditorState => {
  // text item의 경우, 겹침을 피하기 위해 항상 새 track을 생성
  const space = findSpaceForItem({
    durationInFrames: item.durationInFrames,
    startAt: item.from,
    tracks: state.undoableState.tracks,
    startPosition: position,
    stopOnFirstFound: false,
    items: state.undoableState.items,
    itemType: item.type,
  });

  if (
    state.undoableState.items[item.id] &&
    itemOwner(state, item.id)?.track.locked
  )
    return state;
  const targetTrack = state.undoableState.tracks[space.trackIndex];
  const safeSpace =
    targetTrack &&
    (targetTrack.locked ||
      trackCategory(targetTrack, state.undoableState.items) !==
        itemCategory(item))
      ? { ...space, forceCreateNewTrack: true }
      : space;

  const newTracks = addItemInSpace({
    tracks: state.undoableState.tracks,
    item,
    space: safeSpace,
  });

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      tracks: newTracks,
      items: {
        ...state.undoableState.items,
        [item.id]: item,
      },
    },
    selectedItems: select ? [item.id] : state.selectedItems,
  };
};
